import { useState, useEffect } from 'react';
import {
  Flex,
  Box,
  Button,
  Spinner,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LuExternalLink, LuQrCode, LuCopy } from 'react-icons/lu';
import Header from '../components/Header';
import PageContent from '../components/PageContent';
import QrCodeModal from '../components/QrCodeModal';
import ScrapeMenuModal from '../components/ScrapeMenuModal';
import { getMenus, createMenu, duplicateMenu, getTaskStatus, type MenuResponse } from '../api/menus';
import { useAuth } from '../hooks/useAuth';

const Menus = () => {
  const { t } = useTranslation();
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [menus, setMenus] = useState<MenuResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [qrMenu, setQrMenu] = useState<MenuResponse | null>(null);
  const [showScrapeModal, setShowScrapeModal] = useState(false);

  const isAdminUser = user?.email === 'andiskim@gmail.com';

  const fetchMenus = async () => {
    if (!businessId) return;
    try {
      const data = await getMenus(businessId);
      setMenus(data);
    } catch (error) {
      console.error('Failed to load menus:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [businessId]);

  const handleCreateMenu = async () => {
    if (!businessId) return;
    setIsCreating(true);
    try {
      const newMenu = await createMenu({
        business_id: businessId
      });
      toast.success(t('menus.menuCreated'));
      navigate(`/businesses/${businessId}/menus/${newMenu.id}`);
    } catch (error) {
      toast.error(t('menus.createFailed'));
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  const [duplicatingMenuId, setDuplicatingMenuId] = useState<string | null>(null);

  const handleDuplicateMenu = async (e: React.MouseEvent, menuId: string) => {
    e.stopPropagation();
    setDuplicatingMenuId(menuId);
    try {
      const { task_id } = await duplicateMenu(menuId);
      const poll = setInterval(async () => {
        try {
          const task = await getTaskStatus(task_id);
          if (task.status === 'SUCCESS') {
            clearInterval(poll);
            setDuplicatingMenuId(null);
            if (task.result?.status === 'completed' && task.result.menu_id) {
              toast.success(t('menus.menuDuplicated', 'Menu duplicated!'));
              navigate(`/businesses/${businessId}/menus/${task.result.menu_id}`);
            } else {
              toast.error(task.result?.error || 'Duplication failed');
            }
          } else if (task.status === 'FAILURE') {
            clearInterval(poll);
            setDuplicatingMenuId(null);
            toast.error(task.result?.error || 'Duplication failed');
          }
        } catch {
          clearInterval(poll);
          setDuplicatingMenuId(null);
          toast.error('Failed to check duplication status');
        }
      }, 3000);
    } catch (error: any) {
      setDuplicatingMenuId(null);
      toast.error(error.response?.data?.detail || 'Failed to duplicate menu');
    }
  };

  const getMenuDisplayName = (menu: MenuResponse): string => {
    const values = Object.values(menu.name);
    return values[0] || t('menus.untitledMenu');
  };


  return (
    <Flex minHeight="100vh" direction="column" backgroundColor="neutral.900">
      <Header
        businessId={businessId}
        actions={
          <Button
            onClick={isAdminUser ? () => setShowScrapeModal(true) : handleCreateMenu}
            loading={isCreating}
            height="fit-content"
            py="6px"
            px="12px"
            fontWeight="500"
            fontSize="13px"
            color="neutral.900"
            backgroundColor="neutral.100"
            _hover={{ backgroundColor: 'neutral.200' }}
          >
            <Flex alignItems="center" gap="6px">
              {t('menus.createMenu')}
            </Flex>
          </Button>
        }
      />

      <Flex flex="1" direction="column">
        <PageContent>
          <Flex direction="column" gap="32px" maxWidth="1120px" mx="auto" width="100%">
            {/* Content */}
            {isLoading ? (
              <Flex flex="1" justifyContent="center" alignItems="center" py="80px">
                <Spinner size="xl" color="neutral.300" />
              </Flex>
            ) : (
              <Flex direction="column" gap="24px" alignItems="center">
              <Flex gap="24px" flexWrap="wrap" justifyContent="flex-start">
                {menus.map((menu) => (
                  <Box
                    key={menu.id}
                    width="340px"
                    backgroundColor="neutral.800"
                    borderRadius="8px"
                    borderWidth="1px"
                    borderColor="neutral.700"
                    cursor="pointer"
                    transition="all 0.2s"
                    overflow="hidden"
                    opacity={menu.is_active ? 1 : 0.5}
                    _hover={{
                      borderColor: 'neutral.600',
                      opacity: 1,
                    }}
                    onClick={() => navigate(`/businesses/${businessId}/menus/${menu.id}`)}
                  >
                    {/* Menu header image */}
                    <Box
                      height="200px"
                      overflow="hidden"
                      borderBottomWidth="1px"
                      borderColor="neutral.700"
                      backgroundColor={menu.theme?.background_color || 'neutral.700'}
                    >
                      {menu.header_image_url ? (
                        <img
                          src={menu.header_image_url}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <Flex
                          height="100%"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Box fontSize="13px" fontWeight="400" color="neutral.500">
                            {t('menus.noHeaderImage', 'No header image')}
                          </Box>
                        </Flex>
                      )}
                    </Box>

                    {/* Menu info */}
                    <Flex direction="column" gap="4px" p="16px">
                      <Flex alignItems="center" gap="8px">
                        <Box
                          fontSize="14px"
                          fontWeight="500"
                          color="neutral.100"
                        >
                          {getMenuDisplayName(menu)}
                        </Box>
                        {!menu.is_active && (
                          <Box
                            fontSize="11px"
                            fontWeight="500"
                            color="error.600"
                            backgroundColor="error.600/15"
                            px="6px"
                            py="1px"
                            borderRadius="4px"
                            lineHeight="150%"
                          >
                            {t('menus.inactive', 'Inactive')}
                          </Box>
                        )}
                      </Flex>
                      <Box
                        fontSize="12px"
                        fontWeight="400"
                        color="neutral.500"
                      >
                        /m/{menu.slug}
                      </Box>
                      <Flex gap="8px" mt="4px">
                        <Box
                          as="button"

                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            window.open(`${window.location.origin}/m/${menu.slug}`, '_blank');
                          }}
                          onMouseOver={(e: React.MouseEvent) => e.stopPropagation()}
                          display="flex"
                          alignItems="center"
                          gap="4px"
                          fontSize="12px"
                          fontWeight="500"
                          color="neutral.400"
                          bg="transparent"
                          borderWidth="1px"
                          borderColor="neutral.600"
                          borderRadius="4px"
                          px="8px"
                          py="4px"
                          cursor="pointer"
                          _hover={{ backgroundColor: 'neutral.700', color: 'neutral.200' }}
                        >
                          <LuExternalLink size={12} />
                          {t('menus.viewLive', 'View Live')}
                        </Box>
                        <Box
                          as="button"

                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setQrMenu(menu);
                          }}
                          onMouseOver={(e: React.MouseEvent) => e.stopPropagation()}
                          display="flex"
                          alignItems="center"
                          gap="4px"
                          fontSize="12px"
                          fontWeight="500"
                          color="neutral.400"
                          bg="transparent"
                          borderWidth="1px"
                          borderColor="neutral.600"
                          borderRadius="4px"
                          px="8px"
                          py="4px"
                          cursor="pointer"
                          _hover={{ backgroundColor: 'neutral.700', color: 'neutral.200' }}
                        >
                          <LuQrCode size={12} />
                          {t('menus.qrCode', 'QR Code')}
                        </Box>
                        <Box
                          as="button"
                          onClick={(e: React.MouseEvent) => handleDuplicateMenu(e, menu.id)}
                          onMouseOver={(e: React.MouseEvent) => e.stopPropagation()}
                          display="flex"
                          alignItems="center"
                          gap="4px"
                          fontSize="12px"
                          fontWeight="500"
                          color="neutral.400"
                          bg="transparent"
                          borderWidth="1px"
                          borderColor="neutral.600"
                          borderRadius="4px"
                          px="8px"
                          py="4px"
                          cursor={duplicatingMenuId === menu.id ? 'wait' : 'pointer'}
                          opacity={duplicatingMenuId === menu.id ? 0.5 : 1}
                          _hover={{ backgroundColor: 'neutral.700', color: 'neutral.200' }}
                        >
                          {duplicatingMenuId === menu.id ? (
                            <Spinner size="xs" color="neutral.400" />
                          ) : (
                            <LuCopy size={12} />
                          )}
                          {t('menus.duplicate', 'Duplicate')}
                        </Box>
                      </Flex>
                    </Flex>
                  </Box>
                ))}

              </Flex>

              </Flex>
            )}
          </Flex>
        </PageContent>
      </Flex>

      {qrMenu && (
        <QrCodeModal open={!!qrMenu} onClose={() => setQrMenu(null)} slug={qrMenu.slug} />
      )}

      <ScrapeMenuModal
        open={showScrapeModal}
        onClose={() => setShowScrapeModal(false)}
        onSuccess={(menuId) => {
          navigate(`/businesses/${businessId}/menus/${menuId}`);
        }}
      />
    </Flex>
  );
};

export default Menus;
